import { useActionState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { overlay } from 'overlay-kit';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { api, getErrorMessage } from '@/shared/api/api';
import { USER_ROLE } from '@/shared/types/user';
import { Button, Input, Label } from '@/shared/ui';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Separator } from '@/shared/ui/separator';
import { Text } from '@/shared/ui/text';
import { cn } from '@/shared/utils/cn';

const AVAILABLE_EMAIL_DOMAINS = ['dongkoo.co.kr', 'surromind.ai'];
const SIGNUP_DEFAULT_USER_ROLE = USER_ROLE.USER;

const signupSchema = z
  .object({
    email: z
      .string()
      .email({ message: '이메일 주소를 입력하세요.' })
      .refine(
        (email) => {
          const domain = email.split('@')[1];
          return AVAILABLE_EMAIL_DOMAINS.includes(domain);
        },
        { message: '허용되지 않은 이메일 도메인입니다.' }
      ),
    name: z.string().min(1, { message: '이름을 입력하세요.' }),
    password: z
      .string()
      .min(8, { message: '최소 8자 이상 입력하세요.' })
      .regex(/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/'`~;])/, {
        message: '영문, 숫자, 특수문자를 모두 포함해야 합니다.',
      }),
    confirmPassword: z.string().min(1, { message: '비밀번호 확인을 입력하세요.' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: '비밀번호가 일치하지 않습니다.',
    path: ['confirmPassword'],
  });

const initialState = {
  email: '',
  name: '',
  password: '',
  errors: {
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
  },
};

export function SignUpPage() {
  const navigate = useNavigate();

  const { mutate: signup, isPending: loading } = useMutation({
    mutationFn: ({ email, name, password }: { email: string; name: string; password: string }) =>
      api.post<{ user: { email: string; name: string } }>('auth/signup', {
        email,
        name,
        password,
        role: SIGNUP_DEFAULT_USER_ROLE,
      }),
    onSuccess: async () => {
      const response = await overlay.openAsync(({ isOpen, close }) => (
        <Dialog
          onOpenChange={close}
          open={isOpen}
        >
          <DialogContent showCloseButton>
            <DialogHeader>
              <DialogTitle>회원가입 안내</DialogTitle>
              <DialogDescription>관리자의 승인 이후에 로그인 할 수 있어요.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button
                  onClick={() => close(true)}
                  variant="primary"
                >
                  확인
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));

      if (response) {
        navigate('/signin');
      }
    },
    onError: async (error: Error) => {
      const errorMessage = await getErrorMessage(error);
      overlay.open(({ isOpen, close }) => (
        <Dialog
          onOpenChange={close}
          open={isOpen}
        >
          <DialogContent showCloseButton>
            <DialogHeader>
              <DialogTitle>회원가입 안내</DialogTitle>
              <DialogDescription>{errorMessage}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="primary">확인</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ));
    },
  });

  const fn = async (
    prevState: typeof initialState,
    formData: FormData
  ): Promise<typeof initialState> => {
    const email = formData.get('email') as string;
    const name = formData.get('name') as string;
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirm-password') as string;

    const validatedFields = signupSchema.safeParse({ email, password, name, confirmPassword });

    if (!validatedFields.success) {
      return {
        email,
        name,
        password,
        errors: {
          email: validatedFields.error.flatten().fieldErrors.email?.[0] || '',
          name: validatedFields.error.flatten().fieldErrors.name?.[0] || '',
          password: validatedFields.error.flatten().fieldErrors.password?.[0] || '',
          confirmPassword: validatedFields.error.flatten().fieldErrors.confirmPassword?.[0] || '',
        },
      };
    }

    signup({ email, name, password });

    return {
      ...prevState,
      email,
      name,
      password,
      errors: {
        email: '',
        name: '',
        password: '',
        confirmPassword: '',
      },
    };
  };

  const [state, submitForm, isPending] = useActionState(fn, initialState);

  return (
    <>
      <Helmet>
        <title>회원가입 | 동구바이오제약</title>
      </Helmet>
      <div className="w-full max-w-[342px] space-y-7">
        <div className="flex flex-col gap-2 justify-center items-center">
          <Text
            tag="h1"
            typography="typo-title-l-bold"
          >
            회원 가입
          </Text>
        </div>
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col space-y-2">
            <form
              action={submitForm}
              id="signup-form"
            >
              <fieldset className="flex flex-col space-y-2">
                <legend className="sr-only">회원가입</legend>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    autoComplete="off email"
                    className={cn(state.errors.email && 'border-warning')}
                    defaultValue={state.email}
                    id="email"
                    name="email"
                    placeholder="이메일 주소를 입력해주세요."
                    required
                    size="md"
                    type="email"
                  />
                  {state.errors.email && (
                    <Text
                      className="text-warning"
                      tag="p"
                      typography="typo-text-l"
                    >
                      {state.errors.email}
                    </Text>
                  )}
                </div>

                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    autoComplete="off name"
                    className={cn(state.errors.name && 'border-warning')}
                    defaultValue={state.name}
                    id="name"
                    name="name"
                    placeholder="이름을 입력해주세요."
                    required
                    size="md"
                    type="text"
                  />
                  {state.errors.name && (
                    <Text
                      className="text-warning"
                      tag="p"
                      typography="typo-text-l"
                    >
                      {state.errors.name}
                    </Text>
                  )}
                </div>

                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    autoComplete="off current-password"
                    className={cn(state.errors.password && 'border-warning')}
                    defaultValue={state.password}
                    id="password"
                    name="password"
                    placeholder="비밀번호를 입력해주세요."
                    required
                    size="md"
                    type="password"
                  />
                  {state.errors.password && (
                    <Text
                      className="text-warning"
                      tag="p"
                      typography="typo-text-l"
                    >
                      {state.errors.password}
                    </Text>
                  )}
                </div>

                <div className="flex flex-col space-y-1.5">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    autoComplete="off current-password"
                    className={cn(state.errors.confirmPassword && 'border-warning')}
                    id="confirm-password"
                    name="confirm-password"
                    placeholder="비밀번호를 다시 입력해주세요."
                    required
                    size="md"
                    type="password"
                  />
                  {state.errors.confirmPassword && (
                    <Text
                      className="text-warning"
                      tag="p"
                      typography="typo-text-l"
                    >
                      {state.errors.confirmPassword}
                    </Text>
                  )}
                </div>
              </fieldset>
            </form>
          </div>

          <Button
            className="bg-gradient-to-r from-[#2778BE] to-[#8BCC6F]"
            flex
            form="signup-form"
            pending={isPending || loading}
            size="lg"
            type="submit"
          >
            회원 가입
          </Button>

          <div className="flex items-center gap-2 w-full">
            <Separator className="flex-1" />
            <Text
              className="text-muted-foreground text-center"
              tag="p"
              typography="typo-text-xl"
            >
              또는
            </Text>
            <Separator className="flex-1" />
          </div>

          <Text
            className="text-gray-500"
            tag="p"
            typography="typo-text-xl"
          >
            이미 계정이 있으신가요? 있으시다면&nbsp;
            <Link to="/signin">
              <Text
                className="inline hover:underline cursor-pointer text-primary"
                tag="span"
                typography="typo-text-xl-bold"
              >
                로그인
              </Text>
            </Link>
            을 눌러주세요
          </Text>
        </div>
      </div>
    </>
  );
}
